import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import { StepItem } from "../../types/steps";
import VenueDetails from "./steps/VenueDetails";
import InternetDetails from "./steps/InternetDetails";
import AddVenuePhotos from "./steps/AddVenuePhotos2";
import { tokens } from "../../theme";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../../service/Api";
import { useDispatch } from "react-redux";
import { logout } from "../../store/slice/AuthSlice";
import { VenueFormData } from "../../types/venue";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/common/ShowToast";

// Interface for File URLs
interface FileUrls {
  venue: string[];
  paSystem: string[];
  equipment: string[];
  floorPlan: string[];
}

interface VenueDocument {
  id: number;
  file: string;
  file_type: string;
  venue: number;
}

// Interface for API Venue Response
interface ApiVenueResponse {
  id: number;
  name: string;
  venue_type: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  capacity: string;
  layouts: {
    id: number;
    layout_type: string;
    capacity: string;
    created_at: string;
    updated_at: string;
    venue: number;
  }[];
  contacts: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    twitter_handle: string | null;
    instagram_handle: string | null;
    facebook_handle: string | null;
    created_at: string;
    updated_at: string;
    venue: number;
  }[];
  files: { id: number; file: string; file_type: string; venue: number }[];
  has_power: boolean;
  power_type: string;
  power_outage_rate: string;
  power_backup: string;
  backup_power_capacity: string;
  power_distance_from_livestream_desk: string | null;
  internet_service_provider: string | null;
  wifi_name: string;
  wifi_password: string;
  internet_upload_speed: string;
  router_accessibility: string;
  router_distance_from_livestream: string | null;
  has_pa_system: boolean;
  power_socket_type: string;
  pa_system_distance_from_livestream: string;
  pa_system_provider: string;
  other_pa_system_service_provider: string;
  pa_system_contact_name: string;
  pa_system_contact_phone: string;
  pa_system_contact_email: string;
  created_at: string;
  updated_at: string;
}

const EditVenue: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams<{ id: string }>();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<VenueFormData>({
    venueName: "",
    venueType: "",
    addressOne: "",
    addressTwo: "",
    suburb: "",
    city: "",
    capacity: "",
    layouts: [{ layoutType: "", capacity: "" }],
    contact_id: undefined,
    contacts_ids: [],
    contactPersonName: "",
    contactPersonSurname: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    doesVenueHavePower: "",
    powerType: "",
    powerOutageRate: "",
    powerBackup: "",
    backupPowerCapacity: "",
    router_distance_from_livestream: "",
    powerSocketType: "",
    internet_service_provider: "",
    wifi_name: "",
    wifi_password: "",
    internet_upload_speed: "",
    router_accessibility: "",
    has_pa_system: "",
    pa_system_provider: "",
    pa_system_distance_from_livestream: "",
    pa_system_contact_name: "",
    other_pa_system_service_provider: "",
    pa_system_contact_phone: "",
    pa_system_contact_email: "",
    power_distance_from_livestream_desk: "",
    uploadedFiles: {
      venue: [],
      paSystem: [],
      equipment: [],
      floorPlan: [],
    },
  });
  const [fileUrls, setFileUrls] = useState<FileUrls>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });
  const [remainingFileUrls, setRemainingFileUrls] = useState<FileUrls>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });
  const [venueDocuments, setVenueDocuments] = useState<VenueDocument[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps: StepItem[] = [
    { label: "Venue & Power Details" },
    { label: "Internet & PA System" },
    { label: "Venue Photos" },
  ];

  const mapApiResponseToFormData = async (
    apiData: ApiVenueResponse
  ): Promise<VenueFormData> => {
    const contact = apiData.contacts[0] || null;

    if (!contact) {
      console.warn(
        `No contacts found for venue ID ${apiData.id}. Prefilling with empty contact fields.`
      );
    }

    const venueDocuments = apiData.files
      .filter((img) => img.file_type === "venue_photos")
      .map((img) => img.file);
    const paSystemFiles = apiData.files
      .filter((img) => img.file_type === "pa_system")
      .map((img) => img.file);
    const equipmentFiles = apiData.files
      .filter((img) => img.file_type === "equipment")
      .map((img) => img.file);
    const floorPlanFiles = apiData.files
      .filter((img) => img.file_type === "floor_plan")
      .map((img) => img.file);

    setFileUrls({
      venue: venueDocuments,
      paSystem: paSystemFiles,
      equipment: equipmentFiles,
      floorPlan: floorPlanFiles,
    });

    setRemainingFileUrls({
      venue: venueDocuments,
      paSystem: paSystemFiles,
      equipment: equipmentFiles,
      floorPlan: floorPlanFiles,
    });

    setVenueDocuments(apiData.files);

    return {
      venueName: apiData.name?.trim() || "",
      venueType: apiData.venue_type?.trim() || "",
      addressOne: apiData.address_one?.trim() || "",
      addressTwo: apiData.address_two?.trim() || "",
      suburb: apiData.suburb?.trim() || "",
      city: apiData.city?.trim() || "",
      capacity: apiData.capacity?.trim() || "",
      layouts: apiData.layouts?.map((layout) => ({
        layoutType: layout.layout_type?.trim() || "",
        capacity: layout.capacity?.trim() || "",
      })) || [{ layoutType: "", capacity: "" }],
      contact_id: contact?.id ?? undefined,
      contacts_ids: apiData.contacts.map((c) => c.id) || [],
      contactPersonName: contact?.first_name?.trim() || "",
      contactPersonSurname: contact?.last_name?.trim() || "",
      contactPersonEmail: contact?.email?.trim() || "",
      contactPersonPhone: contact?.phone?.trim() || "",
      doesVenueHavePower: apiData.has_power ? "yes" : "no",
      powerType: apiData.power_type?.trim() || "",
      powerOutageRate: apiData.power_outage_rate?.trim() || "",
      powerBackup: apiData.power_backup?.trim() || "",
      backupPowerCapacity: apiData.backup_power_capacity?.trim() || "",
      router_distance_from_livestream:
        apiData.router_distance_from_livestream?.trim() || "",
      powerSocketType: apiData.power_socket_type?.trim() || "",
      internet_service_provider:
        apiData.internet_service_provider?.trim() || "",
      wifi_name: apiData.wifi_name?.trim() || "",
      wifi_password: apiData.wifi_password?.trim() || "",
      internet_upload_speed: apiData.internet_upload_speed || "",
      router_accessibility: apiData.router_accessibility?.trim() || "",
      has_pa_system: apiData.has_pa_system ? "yes" : "no",
      pa_system_provider: apiData.pa_system_provider?.trim() || "",
      pa_system_distance_from_livestream:
        apiData.pa_system_distance_from_livestream?.trim() || "",
      pa_system_contact_name: apiData.pa_system_contact_name?.trim() || "",
      other_pa_system_service_provider:
        apiData.other_pa_system_service_provider?.trim() || "",
      pa_system_contact_phone: apiData.pa_system_contact_phone?.trim() || "",
      pa_system_contact_email: apiData.pa_system_contact_email?.trim() || "",
      power_distance_from_livestream_desk:
        apiData.power_distance_from_livestream_desk?.trim() || "",
      uploadedFiles: {
        venue: [],
        paSystem: [],
        equipment: [],
        floorPlan: [],
      },
    };
  };

  useEffect(() => {
    const fetchVenueData = async () => {
      if (!id) {
        showErrorToast("No venue ID provided");
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await apiService.get<ApiVenueResponse>(
          `/api/venues/venue/${id}/`
        );
        const venueData = await mapApiResponseToFormData(response.data);
        setFormData(venueData);
      } catch (err: any) {
        console.error("Failed to fetch venue data:", err);
        let errorMessage = "Failed to load venue data. Please try again later.";
        if (err.response?.status === 401) {
          errorMessage = "Unauthorized. Please log in again.";
          dispatch(logout());
          navigate("/login");
        }
        showErrorToast(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    };

    fetchVenueData();
  }, [id, dispatch, navigate]);

  const handleInputChange = (field: keyof VenueFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLayoutChange = (
    index: number,
    field: "layoutType" | "capacity",
    value: string
  ) => {
    const updatedLayouts = [...formData.layouts];
    updatedLayouts[index] = { ...updatedLayouts[index], [field]: value };
    setFormData((prev) => ({
      ...prev,
      layouts: updatedLayouts,
    }));
  };

  const addLayout = () => {
    setFormData((prev) => ({
      ...prev,
      layouts: [...prev.layouts, { layoutType: "", capacity: "" }],
    }));
  };

  const handleFilesChange =
    (category: keyof VenueFormData["uploadedFiles"]) =>
    async (files: File[]) => {
      const validFiles = files.filter(
        (file) => file instanceof File && file.size > 0 && file.name
      );
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: {
          ...prev.uploadedFiles,
          [category]: validFiles,
        },
      }));
    };

  const handleExistingFilesChange = (
    category: keyof VenueFormData["uploadedFiles"],
    remainingFiles: string[]
  ) => {
    setRemainingFileUrls((prev) => ({
      ...prev,
      [category]: remainingFiles,
    }));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveStep(newValue);
  };

  const handleSubmit = async () => {
    if (!id) {
      showErrorToast("No venue ID provided");
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("=== Debug: File States ===");
      console.log("Original fileUrls:", fileUrls);
      console.log("Remaining fileUrls (after user edits):", remainingFileUrls);
      console.log(
        "New uploads (formData.uploadedFiles):",
        formData.uploadedFiles
      );
      const categoryMap: Record<keyof FileUrls, string> = {
        venue: "venue",
        paSystem: "pa_system",
        equipment: "equipment",
        floorPlan: "floor_plan",
      };

      const deletePromises: Promise<any>[] = [];
      for (const cat in categoryMap) {
        const category = cat as keyof FileUrls;
        const backendType = categoryMap[category];
        const originalUrls = fileUrls[category];
        const keptUrls = remainingFileUrls[category];
        const deletedUrls = originalUrls.filter(
          (url) => !keptUrls.includes(url)
        );

        console.log(`Category: ${category}`);
        console.log("Original URLs:", originalUrls);
        console.log("Kept URLs:", keptUrls);
        console.log("Files marked for deletion:", deletedUrls);

        for (const delUrl of deletedUrls) {
          const doc = venueDocuments.find(
            (d) => d.file === delUrl && d.file_type === backendType
          );
          if (doc) {
            deletePromises.push(
              apiService.delete(`/api/venues/document/${doc.id}/`)
            );
          }
        }
      }
      await Promise.all(deletePromises);

      const formDataToSend = new FormData();
    

      const validLayouts = formData.layouts.filter(
        (layout) => layout.layoutType.trim() !== ""
      );

      const payload = {
        name: formData.venueName,
        venue_type: formData.venueType,
        address_one: formData.addressOne,
        address_two: formData.addressTwo,
        suburb: formData.suburb,
        city: formData.city,
        capacity: formData.capacity,
        layouts: validLayouts,
        contact_id: formData.contact_id,
        contacts_ids: [
          ...(Array.isArray(formData.contacts_ids)
            ? formData.contacts_ids
            : []),
          ...(formData.contact_id ? [Number(formData.contact_id)] : []),
        ],
        contact_person_name: formData.contactPersonName,
        contact_person_surname: formData.contactPersonSurname,
        contact_person_email: formData.contactPersonEmail,
        contact_person_phone: formData.contactPersonPhone,
        has_power: formData.doesVenueHavePower === "yes",
        power_type: formData.powerType || " ",
        power_outage_rate: formData.powerOutageRate || " ",
        power_backup: formData.powerBackup || " ",
        backup_power_capacity: formData.backupPowerCapacity || " ",
        router_distance_from_livestream:
          formData.router_distance_from_livestream
            ? parseInt(formData.router_distance_from_livestream)
            : null,
        power_socket_type: formData.powerSocketType || " ",
        internet_service_provider: formData.internet_service_provider,
        wifi_name: formData.wifi_name,
        wifi_password: formData.wifi_password,
        internet_upload_speed: formData.internet_upload_speed,
        router_accessibility: formData.router_accessibility,
        has_pa_system: formData.has_pa_system === "yes",
        pa_system_provider: formData.pa_system_provider,
        pa_system_distance_from_livestream:
          formData.pa_system_distance_from_livestream
            ? parseInt(formData.pa_system_distance_from_livestream)
            : null,
        pa_system_contact_name: formData.pa_system_contact_name,
        other_pa_system_service_provider:
          formData.other_pa_system_service_provider,
        pa_system_contact_phone: formData.pa_system_contact_phone,
        pa_system_contact_email: formData.pa_system_contact_email,
        power_distance_from_livestream_desk:
          formData.power_distance_from_livestream_desk
            ? parseInt(formData.power_distance_from_livestream_desk)
            : " ",
      };
      console.log("Payload before sending:", payload);
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, value]) => {
          if (value === null || value === undefined || value === "") {
            return false;
          }
          return true;
        })
      );

      formDataToSend.append("data", JSON.stringify(filteredPayload));
      console.log("Appending new venue files:", formData.uploadedFiles.venue);
      console.log(
        "Appending new PA system files:",
        formData.uploadedFiles.paSystem
      );
      console.log(
        "Appending new equipment files:",
        formData.uploadedFiles.equipment
      );
      console.log(
        "Appending new floor plan files:",
        formData.uploadedFiles.floorPlan
      );

      let hasFiles = false;
      if (formData.uploadedFiles.venue.length > 0) {
        console.log("FormData keys to be sent:");
        formData.uploadedFiles.venue.forEach((file: File, index: number) => {
          if (
            file instanceof File &&
            file.size > 0 &&
            file.name &&
            file.type.startsWith("image/")
          ) {
            formDataToSend.append(`venue_photos[${index}]`, file);
            hasFiles = true;
          } else {
            console.warn(
              `Skipping invalid venue file at index ${index}:`,
              file
            );
          }
        });
      }
      if (formData.uploadedFiles.paSystem.length > 0) {
        console.log("FormData keys to be sent:");
        formData.uploadedFiles.paSystem.forEach((file: File, index: number) => {
          if (
            file instanceof File &&
            file.size > 0 &&
            file.name &&
            file.type.startsWith("image/")
          ) {
            formDataToSend.append(`pa_system_photos[${index}]`, file);
            hasFiles = true;
          } else {
            console.warn(
              `Skipping invalid paSystem file at index ${index}:`,
              file
            );
          }
        });
      }
      if (formData.uploadedFiles.equipment.length > 0) {
        console.log("FormData keys to be sent:");
        formData.uploadedFiles.equipment.forEach(
          (file: File, index: number) => {
            if (
              file instanceof File &&
              file.size > 0 &&
              file.name &&
              file.type.startsWith("image/")
            ) {
              formDataToSend.append(`equipment_photos[${index}]`, file);
              hasFiles = true;
            } else {
              console.warn(
                `Skipping invalid equipment file at index ${index}:`,
                file
              );
            }
          }
        );
      }
      if (formData.uploadedFiles.floorPlan.length > 0) {
        console.log("FormData keys to be sent:");
        formData.uploadedFiles.floorPlan.forEach(
          (file: File, index: number) => {
            if (
              file instanceof File &&
              file.size > 0 &&
              file.name &&
              (file.type.startsWith("image/") ||
                file.type === "application/pdf")
            ) {
              formDataToSend.append(`floor_plan_photos[${index}]`, file);
              hasFiles = true;
            } else {
              console.warn(
                `Skipping invalid floorPlan file at index ${index}:`,
                file
              );
            }
          }
        );
      }

      if (!hasFiles) {
        console.warn(
          "No valid files to submit. Sending payload without files."
        );
      }

      const response = await apiService.put(
        `/api/venues/venue/${id}/full-update/`,
        formDataToSend,
        false
      );

      showSuccessToast("Venue updated successfully!");
      setTimeout(() => {
        navigate(`/venues/view/${id}`);
      }, 2000);
    } catch (error: any) {
      console.error("Error updating venue:", error, {
        responseData: error.response?.data,
        status: error.response?.status,
      });
      let errorMessage = "Failed to update venue. Please try again.";
      if (error.response?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
        if (error.response.data.errors) {
          const fileErrors = error.response.data.errors;
          const errorDetails = Object.entries(fileErrors)
            .map(([field, errors]) => {
              if (Array.isArray(errors)) {
                return `${field}: ${errors.join(", ")}`;
              } else if (typeof errors === "object" && errors !== null) {
                return `${field}: ${Object.entries(errors)
                  .map(([index, messages]) => {
                    if (Array.isArray(messages)) {
                      return `File ${index}: ${messages.join(", ")}`;
                    } else if (typeof messages === "string") {
                      return `File ${index}: ${messages}`;
                    } else {
                      return `File ${index}: ${JSON.stringify(messages)}`;
                    }
                  })
                  .join("; ")}`;
              }
              return `${field}: ${JSON.stringify(errors)}`;
            })
            .join("; ");
          errorMessage += `: ${errorDetails}`;
        }
      } else if (error.response?.data) {
        errorMessage = JSON.stringify(error.response.data);
      }
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (isSubmitting && activeStep !== steps.length - 1) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress sx={{ color: colors.primary[400] }} />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <Box>
            <VenueDetails
              setFormData={setFormData}
              formData={formData}
              handleInputChange={(field, value) =>
                handleInputChange(field as keyof VenueFormData, value)
              }
              handleLayoutChange={(index, field, value) =>
                handleLayoutChange(
                  index,
                  field as "layoutType" | "capacity",
                  value
                )
              }
              addLayout={addLayout}
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <InternetDetails
              formData={formData}
              handleInputChange={(field, value) =>
                handleInputChange(field as keyof VenueFormData, value)
              }
              handleLayoutChange={(index, field, value) =>
                handleLayoutChange(
                  index,
                  field as "layoutType" | "capacity",
                  value
                )
              }
              setFormData={setFormData}
              addLayout={addLayout}
            />
          </Box>
        );
      case 2:
        return (
          <Box>
            <AddVenuePhotos
              uploadedFiles={formData.uploadedFiles}
              handleFilesChange={handleFilesChange}
              existingFiles={fileUrls}
              onExistingFilesChange={handleExistingFilesChange}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 4, maxWidth: 1200, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          value={activeStep}
          onChange={handleTabChange}
          aria-label="Venue Setup Tabs"
        >
          {steps.map((step, index) => (
            <Tab key={index} label={step.label} value={index} />
          ))}
        </Tabs>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            color: colors.primary[900],
            backgroundColor: colors.primary[400],
          }}
          startIcon={
            isSubmitting && <CircularProgress size={20} color="inherit" />
          }
        >
          Update Venue
        </Button>
      </Box>
      <Box sx={{ minHeight: 400, py: 2 }}>{renderStepContent()}</Box>
    </Paper>
  );
};

export default EditVenue;
